import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../lib/cn.js';
import { useFieldControl } from './Field.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * INPUT — A TEXT-LIKE CONTROL. Follows the five rules in `Button.tsx`.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── THERE IS NO `invalid` PROP, AND THAT IS THE DESIGN ─────────────────────
 * The invalid *state* is `aria-invalid` and the invalid *styling* is a variant keyed off that
 * same attribute, so there is exactly one source of truth and no way to render a red box that
 * the accessibility tree believes is fine. A boolean prop would be a second source, and the
 * two would eventually disagree — which is the failure that looks like nothing at all to the
 * person who wrote it.
 *
 * ── THE VARIANT IS NOT THE OBVIOUS ONE, AND THE OBVIOUS ONE IS A BUG ───────
 * `aria-invalid:border-danger` does compile (checked against the installed Tailwind rather
 * than assumed), and it compiles to `[aria-invalid="true"]` — an exact string match on `true`.
 *
 * But `aria-invalid` is an ARIA *token* attribute: its values are `true`, `false`, `grammar`
 * and `spelling`, and every one of them except `false` means invalid. So the obvious variant
 * styles a `true` field correctly and leaves `aria-invalid="spelling"` looking perfectly
 * healthy while a screen reader announces "invalid spelling" — the eye and the accessibility
 * tree disagreeing, which is the exact failure the no-`invalid`-prop rule above exists to make
 * impossible. Composing `aria-[invalid]` with `not-aria-[invalid=false]` emits
 * `[aria-invalid]:not([aria-invalid="false"])`, which is that ARIA rule written out.
 *
 * The border is not the only signal either. Colour alone would fail WCAG 1.4.1; the message
 * `Field` renders below the control is the non-colour carrier, and it is why an invalid
 * `Input` outside a `Field` is a thing to review rather than a thing to style around.
 *
 * ── ADORNMENTS ARE `aria-hidden`, LIKE `Button`'s ICONS ────────────────────
 * An adornment is positioned over the input, not inside it: as a sibling it belongs to no
 * accessible name and no description, so a screen reader in forms mode reaches the input and
 * never encounters it. Rather than leave that half-communicated, it is hidden outright and the
 * rule is explicit — anything an adornment means ("$", "ms", "ext.") must also be in the
 * `Field`'s label or description, where it is actually connected to the control.
 */

export type InputSize = 'sm' | 'md' | 'lg';

interface SizeSpec {
  /** Base metrics. `min-h` not `h` — see `Button.SIZES`; a fixed height clips at large text. */
  base: string;
  /** Horizontal room made for an adornment; overrides `base`'s padding on that side. */
  withLeading: string;
  withTrailing: string;
  /** Insets the adornment to the same gutter the text sits in, so the two line up. */
  adornment: string;
}

/**
 * `pl-*`/`pr-*` rather than `px-*`, and this is not stylistic. `tailwind-merge` treats `px` as
 * conflicting with `pl` only when `px` comes LAST, so a base `px-3` followed by `pl-9` keeps
 * BOTH classes — and in Tailwind 4 `px-*` emits logical `padding-inline` while `pl-*` emits
 * physical `padding-left`, equal specificity, so which one wins is decided by the order
 * Tailwind happened to emit them in the sheet. Splitting the sides up front means the merge is
 * a real merge and the adornment never lands on top of the text.
 */
const SIZES: Record<InputSize, SizeSpec> = {
  sm: {
    base: 'min-h-8 py-1 pl-2.5 pr-2.5 text-sm',
    withLeading: 'pl-8',
    withTrailing: 'pr-8',
    adornment: 'px-2.5',
  },
  md: {
    base: 'min-h-10 py-2 pl-3 pr-3 text-sm',
    withLeading: 'pl-9',
    withTrailing: 'pr-9',
    adornment: 'px-3',
  },
  lg: {
    base: 'min-h-12 py-2.5 pl-3.5 pr-3.5 text-base',
    withLeading: 'pl-10',
    withTrailing: 'pr-10',
    adornment: 'px-3.5',
  },
};

/**
 * `size` is omitted from the native props and redefined. HTML's `size` is a width hint measured
 * in characters, from before CSS could do it; keeping it would mean this component's only
 * scale prop had to be called something else, and every caller would have to remember which of
 * the two they meant. Set a width with `className` instead.
 */
export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: InputSize;
  /** Decorative only — `aria-hidden`. See the header. */
  leading?: ReactNode;
  trailing?: ReactNode;
  /** Class for the positioning wrapper. `className` goes to the `<input>` itself. */
  wrapperClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    size = 'md',
    leading,
    trailing,
    wrapperClassName,
    className,
    id,
    required,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    ...rest
  },
  ref,
) {
  const field = useFieldControl();
  const spec = SIZES[size];

  /**
   * The Field's id WINS over a caller-supplied one, which is the opposite of the usual
   * precedence and is deliberate. The `<label for>` was committed to the Field's id before this
   * component rendered; honouring an `id` prop here would leave the label pointing at an
   * element that no longer exists and the control with no accessible name — a silent
   * regression from a prop that looks entirely innocent. `Field`'s `controlId` is the supported
   * way to choose the id, because it moves both ends at once.
   */
  const resolvedId = field?.id ?? id;

  /**
   * MERGED, not replaced. A caller's own `aria-describedby` is additional information, not a
   * replacement for the field's description and error — and `describedby` is a list precisely
   * so both can be true.
   */
  const resolvedDescribedBy =
    [field?.['aria-describedby'], ariaDescribedBy].filter(Boolean).join(' ') || undefined;

  // A caller's explicit value wins here: `aria-invalid="spelling"` is more specific than the
  // Field's plain `true`, and nothing about the Field contradicts it.
  const resolvedInvalid = ariaInvalid ?? field?.['aria-invalid'];

  return (
    <div className={cn('relative', wrapperClassName)}>
      {leading ? (
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-y-0 left-0 flex items-center text-fg-muted',
            spec.adornment,
          )}
        >
          {leading}
        </span>
      ) : null}

      <input
        ref={ref}
        id={resolvedId}
        required={required ?? field?.required}
        aria-describedby={resolvedDescribedBy}
        aria-invalid={resolvedInvalid}
        className={cn(
          'block w-full rounded-md border border-border-strong bg-surface text-fg',
          // `text-fg-subtle` is held to 4.5:1 like every other text token (see tokens.css), so
          // placeholder text stays legible — most design systems reach for a lighter grey here
          // and quietly drop below the threshold.
          'placeholder:text-fg-subtle',
          'transition-colors duration-150',
          // Distinct treatments because they mean different things: `disabled` is "not
          // applicable", `readOnly` is "applicable, and still focusable, copyable, readable".
          'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:opacity-60',
          'read-only:bg-surface-sunken',
          'aria-[invalid]:not-aria-[invalid=false]:border-danger',
          spec.base,
          leading ? spec.withLeading : null,
          trailing ? spec.withTrailing : null,
          className,
        )}
        {...rest}
      />

      {trailing ? (
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-y-0 right-0 flex items-center text-fg-muted',
            spec.adornment,
          )}
        >
          {trailing}
        </span>
      ) : null}
    </div>
  );
});
